const DEFAULT_BASE_URL = '/cc/API/PaymentInstrument/';

// Root-relative by default, which resolves correctly only while the page is served from
// the Client Center origin. Callers hosted elsewhere must override it — see
// setClientCenterBaseUrl.
let baseUrl: string = DEFAULT_BASE_URL;

export type PaymentApiPrimitiveId = string | number;

interface CustomerScopedPaymentApiRequest {
  CustomerId: PaymentApiPrimitiveId;
}

interface WalletRequestReference {
  WalletRequestId: PaymentApiPrimitiveId;
}

interface PaymentInstrumentReference {
  PaymentInstrumentId: PaymentApiPrimitiveId;
}

interface PaymentRequestReference {
  PaymentRequestId: PaymentApiPrimitiveId;
}

interface ClientAccessTokenResponse {
  ClientAccessToken: string;
}

export interface PaymentApiPostRequest<TData> {
  url: string;
  data: TData;
  silent: boolean;
}

export interface PaymentApiClient {
  post<TResponse, TData>(request: PaymentApiPostRequest<TData>): Promise<TResponse>;
}

export interface CreateWalletReqRequest extends CustomerScopedPaymentApiRequest {
  CTPPIId?: PaymentApiPrimitiveId;
}

export type CreateWalletReqResponse = ClientAccessTokenResponse;

export interface CreateWalletReqResult {
  token: string;
}

export interface WalletRequestValidateRequest
  extends CustomerScopedPaymentApiRequest, WalletRequestReference {
  IsPaymentInstrumentUpdate?: boolean;
}

export interface WalletRequestValidateResponse
  extends ClientAccessTokenResponse, PaymentInstrumentReference, PaymentRequestReference {
  ValidationStatus: string;
}

export type PaymentInstrumentAssociateRequest = CustomerScopedPaymentApiRequest & WalletRequestReference;

export interface PaymentInstrumentAssociateResponse {
  Id: PaymentApiPrimitiveId;
}

export interface PaymentInstrumentAssociateV2Request
  extends CustomerScopedPaymentApiRequest, PaymentInstrumentReference, PaymentRequestReference {
  FriendlyName?: string;
  IsPaymentInstrumentUpdate?: boolean;
}

export interface PaymentInstrumentAssociateV2Response {
  PaymentInstrId: PaymentApiPrimitiveId;
}

export interface PaymentInstrumentAssociateResult {
  mtPiId: PaymentApiPrimitiveId;
}

export interface CreatePaymentReqRequest
  extends CustomerScopedPaymentApiRequest, PaymentInstrumentReference {
  AccountId: PaymentApiPrimitiveId;
  ChargeAmount: number;
  IsSubscription: boolean;
}

export interface CreatePaymentReqResponse extends ClientAccessTokenResponse, PaymentRequestReference {
  Status: string;
}

export interface PaymentApiErrorResponse {
  data?: {
    Errors?: Array<{
      Message?: string;
    }>;
  };
}

const Routes = {
  CreateWalletReq: 'CreateWalletReq',
  WalletRequestValidate: 'WalletRequestValidate',
  PaymentInstrumentAssociate: 'PaymentInstrumentAssociate',
  PaymentInstrumentAssociateV2: 'PaymentInstrumentAssociateV2',
  CreatePaymentReq: 'CreatePaymentReq',
};

/**
 * Point these endpoints at an explicit Client Center origin.
 *
 * These are POSTs. Left root-relative on a page served from another origin they are
 * answered with a redirect, and a redirect turns the POST into a GET without its body,
 * so the server rejects the request as invalid. Pass the Client Center base URL
 * (e.g. `https://ads.microsoft.com/cc/`) to address the service directly.
 *
 * Call with no argument to restore the default.
 */
export function setClientCenterBaseUrl(ccBaseUrl?: string | null): void {
  baseUrl = ccBaseUrl
    ? `${ccBaseUrl.replace(/\/+$/, '')}/API/PaymentInstrument/`
    : DEFAULT_BASE_URL;
}

function endpoint(route: string): string {
  return `${baseUrl}${route}`;
}

function getFilteredSearchParams(): string {
  const params = new URLSearchParams(window.location.search);
  const filteredParams = new URLSearchParams();

  const uid = params.get('uid');
  const cid = params.get('cid');

  if (uid) filteredParams.set('uid', uid);
  if (cid) filteredParams.set('cid', cid);

  return `?${filteredParams.toString()}`;
}

function appendSearchParams(url: string): string {
  const filteredParams = getFilteredSearchParams();
  return `${url}${filteredParams}`;
}

export function createWalletReq(ajaxPromise: PaymentApiClient, {
  CustomerId,
  CTPPIId,
}: CreateWalletReqRequest): Promise<CreateWalletReqResult> {
  const url = appendSearchParams(endpoint(Routes.CreateWalletReq));

  return ajaxPromise.post<CreateWalletReqResponse, CreateWalletReqRequest>({
    url,
    data: {
      CustomerId,
      CTPPIId,
    },
    silent: true,
  }).then(res => ({
    token: res.ClientAccessToken,
  }));
}

export function walletRequestValidate(ajaxPromise: PaymentApiClient, {
  CustomerId,
  WalletRequestId,
  IsPaymentInstrumentUpdate,
}: WalletRequestValidateRequest): Promise<WalletRequestValidateResponse> {
  const url = appendSearchParams(endpoint(Routes.WalletRequestValidate));

  return ajaxPromise.post<WalletRequestValidateResponse, WalletRequestValidateRequest>({
    url,
    data: {
      CustomerId,
      WalletRequestId,
      IsPaymentInstrumentUpdate,
    },
    silent: true,
  }).then(res => ({
    ClientAccessToken: res.ClientAccessToken,
    PaymentInstrumentId: res.PaymentInstrumentId,
    PaymentRequestId: res.PaymentRequestId,
    ValidationStatus: res.ValidationStatus,
  })).catch((res: PaymentApiErrorResponse) => {
    const errorMessage = res && res.data && res.data.Errors && res.data.Errors[0] && res.data.Errors[0].Message;
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errorMessage });
  });
}

export function associatePaymentInstrument(ajaxPromise: PaymentApiClient, {
  CustomerId,
  WalletRequestId,
}: PaymentInstrumentAssociateRequest): Promise<PaymentInstrumentAssociateResult> {
  const url = appendSearchParams(endpoint(Routes.PaymentInstrumentAssociate));

  return ajaxPromise.post<PaymentInstrumentAssociateResponse, PaymentInstrumentAssociateRequest>({
    url,
    data: {
      CustomerId,
      WalletRequestId,
    },
    silent: true,
  }).then(res => ({
    mtPiId: res.Id,
  })).catch((res: PaymentApiErrorResponse) => {
    const errorMessage = res && res.data && res.data.Errors && res.data.Errors[0] && res.data.Errors[0].Message;
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errorMessage });
  });
}

export function paymentInstrumentAssociateV2(ajaxPromise: PaymentApiClient, {
  CustomerId,
  FriendlyName,
  PaymentInstrumentId,
  PaymentRequestId,
  IsPaymentInstrumentUpdate,
}: PaymentInstrumentAssociateV2Request): Promise<PaymentInstrumentAssociateResult> {
  const url = appendSearchParams(endpoint(Routes.PaymentInstrumentAssociateV2));

  return ajaxPromise.post<PaymentInstrumentAssociateV2Response, PaymentInstrumentAssociateV2Request>({
    url,
    data: {
      CustomerId,
      FriendlyName,
      PaymentInstrumentId,
      PaymentRequestId,
      IsPaymentInstrumentUpdate,
    },
    silent: true,
  }).then(res => ({
    mtPiId: res.PaymentInstrId,
  })).catch((res: PaymentApiErrorResponse) => {
    const errorMessage = res && res.data && res.data.Errors && res.data.Errors[0] && res.data.Errors[0].Message;
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errorMessage });
  });
}

export function createPaymentReq(ajaxPromise: PaymentApiClient, {
  AccountId,
  ChargeAmount,
  CustomerId,
  IsSubscription,
  PaymentInstrumentId,
}: CreatePaymentReqRequest): Promise<CreatePaymentReqResponse> {
  const url = appendSearchParams(endpoint(Routes.CreatePaymentReq));

  return ajaxPromise.post<CreatePaymentReqResponse, CreatePaymentReqRequest>({
    url,
    data: {
      AccountId,
      ChargeAmount,
      CustomerId,
      IsSubscription,
      PaymentInstrumentId,
    },
    silent: true,
  }).then(res => ({
    ClientAccessToken: res.ClientAccessToken,
    PaymentRequestId: res.PaymentRequestId,
    Status: res.Status,
  })).catch((res: PaymentApiErrorResponse) => {
    const errorMessage = res && res.data && res.data.Errors && res.data.Errors[0] && res.data.Errors[0].Message;
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errorMessage });
  });
}
